import { useCallback, useEffect, useRef, useState } from "react";
import {
  Banner,
  BannerTypes,
  ContactCardCheckout,
  LoadingOverlay,
  Typography,
} from "@sunrun/experience-ui-components";
import type {
  Contact,
  Role,
} from "@sunrun/experience-ui-components/lib/components/ContactCard";
import { useLocation, useNavigate } from "react-router-dom";
import qs from "qs";
import { modals } from "fixtures/modals";
import { useContactManagement } from "hooks/useContactManagement";
import { ROLES } from "utils/usageUtils";
import { voidAllProposals, regenDocs } from "services/prospect";
import { useAppSelector } from "store";
import { useCheckoutContext } from "checkout/providers/CheckoutContext";
import { useProposals } from "utils/swrHooks";
import { useReadSignDocs } from "checkout/hooks/useReadSignDocs";
import { rollbar } from "providers/rollbar";
import {
  isNemAgreementCheckMessage,
  isTrustAgreementNameMatchMessage,
} from "utils/contractReviewUtils";
import { ContractReviewBanners } from "components/molecules/ContractReviewBanners";

const {
  REGEN_INFORM,
  REGEN_CONFIRM,
  VOID_SUNRUN,
  SAME_SIGNER,
  SAME_SIGNER_THEN_VOID_SUNRUN,
  SAME_SIGNER_THEN_RESEND_SUNRUN,
  SAME_SIGNER_THEN_REGEN_INFORM,
  SAME_SIGNER_THEN_REGEN_CONFIRM,
  REMOVE_SECONDARY,
  RESEND_SUNRUN_SECONDARY,
  RESEND_SUNRUN_UTILITY,
} = modals;

type CheckoutContactsProps = {
  selectedPrimaryContact: Contact | undefined;
  setSelectedPrimaryContact: (contact: Contact) => Promise<void>;
  selectedUtilityContact: Contact | undefined;
  setSelectedUtilityContact: (contact: Contact) => Promise<void>;
  selectedSecondaryContact: Contact | undefined;
  hideUtilityContactCard?: boolean;
  setSelectedSecondaryContact: (
    contact: Contact,
    remove: boolean
  ) => Promise<void>;
  refSetter?: (node: HTMLDivElement | null) => void;
};

const CheckoutContacts = ({
  selectedPrimaryContact,
  setSelectedPrimaryContact,
  selectedUtilityContact,
  setSelectedUtilityContact,
  selectedSecondaryContact,
  setSelectedSecondaryContact,
  refSetter,
}: CheckoutContactsProps) => {
  const location = useLocation();
  const secondaryRef = useRef<HTMLDivElement>(null);
  const secondaryEditState = Boolean(
    qs.parse(location.search.replace("?", ""))?.secondaryEdit
  );

  const [isVoiding, setIsVoiding] = useState(false);
  const [isRegeneratingDocuments, setIsRegeneratingDocuments] = useState(false);
  const [isNew, setIsNewContact] = useState(false);
  const [saveOverlayMessage, setSaveOverlayMessage] = useState<string | null>(
    null
  );

  const {
    offer,
    proposalId,
    prospectId,
    setShowSecondaryWarningBanner,
    setShowUtilityBillWarningBanner,
    refreshTasks,
    contractReviewMessages,
    isNewHomes,
  } = useCheckoutContext();
  const navigate = useNavigate();

  const { authKey, identity, cognitoToken } = useAppSelector((state) => ({
    authKey: state?.auth?.hybridToken,
    identity: state?.auth?.identity,
    cognitoToken: state?.auth?.cognitoToken?.token,
  }));

  const {
    contactList,
    onAddContact: saveContact,
    onEditContact: editContact,
    loading: updateContactLoading,
  } = useContactManagement();
  const { data: proposals, mutate: refreshProposals } = useProposals(
    prospectId!,
    authKey
  );
  const { readSignDocsSteps } = useReadSignDocs({ prospectId, proposalId });

  const findContactById = (id: string) =>
    contactList?.find((contact) => contact.contactId === id);

  const hasSignedProposal = !!proposals?.find(({ signed }) => signed);
  const offerApproved = !!proposals?.find(
    ({ stage }) => "SR Approved" === stage
  );
  const hasProposals = !!proposals?.length;
  const isNEMSigned = !!readSignDocsSteps?.find(
    (step) => step.odiName === "nemAgreement"
  )?.completed;

  useEffect(() => {
    if (secondaryEditState) {
      secondaryRef?.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [secondaryEditState]);

  const voidAllAndNavigate = useCallback(
    async (authKey: string, prospectId: string) => {
      setIsVoiding(true);
      try {
        await voidAllProposals({
          authKey,
          prospectId,
          identity,
          cognitoToken: cognitoToken ?? "",
        });
        navigate(`/prospect/${prospectId}/offer/list/`);
      } catch (error: unknown) {
        rollbar.error(error as any);
      } finally {
        setIsVoiding(false);
      }
    },
    [identity, cognitoToken, navigate]
  );

  const applyVoidOrRegenForPrimaryChange = useCallback(
    async (nextContactId?: string) => {
      if (nextContactId === selectedPrimaryContact?.contactId) {
        return;
      }

      if (offerApproved) {
        await voidAllAndNavigate(authKey, prospectId);
        return;
      }

      setIsRegeneratingDocuments(true);
      await regenDocs({
        authKey,
        prospectId,
      });
      await Promise.all([refreshProposals(), refreshTasks()]);
      setIsRegeneratingDocuments(false);
      setIsVoiding(false);
    },
    [
      selectedPrimaryContact?.contactId,
      offerApproved,
      voidAllAndNavigate,
      authKey,
      prospectId,
      refreshProposals,
      refreshTasks,
    ]
  );

  const onContactInfoSave = useCallback(
    async (newContact: Contact, role: Role) => {
      const {
        contactId,
        customerFirstName,
        customerLastName,
        customerPrimaryPhone,
        customerEmail,
      } = newContact;

      const isEditingAnyName = !!contactList?.find((contact) => {
        return (
          contact.contactId === contactId &&
          (contact.customerFirstName !== customerFirstName ||
            contact.customerLastName !== customerLastName)
        );
      });

      if (isEditingAnyName || !contactId) {
        setIsNewContact(true);
        setSaveOverlayMessage("Creating contact...");
        try {
          const savedContact = await saveContact({
            customerEmail,
            customerFirstName,
            customerLastName,
            customerPrimaryPhone,
            role,
            ...(role === ROLES.HOMEOWNER && { primary: true }),
          });

          if (savedContact) {
            if (role === ROLES.HOMEOWNER) {
              if (
                savedContact.contactId !== selectedPrimaryContact?.contactId
              ) {
                setSaveOverlayMessage(
                  offerApproved
                    ? "Voiding offers..."
                    : "Regenerating documents..."
                );
              }
              await applyVoidOrRegenForPrimaryChange(savedContact.contactId);
            } else if (role === ROLES.CONTRACTCOSIGNER && hasSignedProposal) {
              setShowSecondaryWarningBanner(true);
            } else if (role === ROLES.UTILITYBILLCONTACT && isNEMSigned) {
              setShowUtilityBillWarningBanner(true);
            }
          }

          return savedContact;
        } finally {
          setIsNewContact(false);
          setSaveOverlayMessage(null);
        }
      } else {
        return editContact(newContact);
      }
    },
    [
      contactList,
      saveContact,
      hasSignedProposal,
      isNEMSigned,
      selectedPrimaryContact?.contactId,
      applyVoidOrRegenForPrimaryChange,
      offerApproved,
      setShowSecondaryWarningBanner,
      setShowUtilityBillWarningBanner,
      editContact,
    ]
  );

  const handlePrimarySelectionUpdate = async (selectedContactId: string) => {
    const selectedContact = findContactById(selectedContactId);
    selectedContact && (await setSelectedPrimaryContact(selectedContact));
    if (selectedContactId !== selectedPrimaryContact?.contactId) {
      if (offerApproved) {
        await voidAllAndNavigate(authKey, prospectId);
      } else {
        setIsRegeneratingDocuments(true);
        await regenDocs({
          authKey,
          prospectId,
        });
        await Promise.all([refreshProposals(), refreshTasks()]);
        setIsRegeneratingDocuments(false);
      }
    }
    setIsVoiding(false);
  };

  const handleSecondarySelectionUpdate = async (selectedContactId: string) => {
    const selectedContact = findContactById(selectedContactId);

    if (selectedContact) {
      await setSelectedSecondaryContact(selectedContact, false);
    } else {
      // If selectedContact is undefined, it means the user is trying to remove the secondary signer
      await setSelectedSecondaryContact(selectedSecondaryContact!, true);
    }

    if (
      selectedContactId !== selectedSecondaryContact?.contactId &&
      hasSignedProposal
    ) {
      setShowSecondaryWarningBanner(true);
    }
  };

  const handleBillingSelectionUpdate = async (selectedContactId: string) => {
    const selectedContact = findContactById(selectedContactId);

    selectedContact && (await setSelectedUtilityContact(selectedContact));
    if (
      selectedContactId !== selectedUtilityContact?.contactId &&
      isNEMSigned
    ) {
      setShowUtilityBillWarningBanner(true);
    }
  };

  return (
    <>
      <div ref={refSetter}>
        <ContactCardCheckout
          selectedContactId={selectedPrimaryContact?.contactId}
          toolTipText="The primary signer must be on the title of the home and represent the legal rights surrounding the ownership and use of the residential property."
          contactList={contactList || []}
          contactType="primary"
          hasOffer={hasSignedProposal}
          hasProposals={hasProposals}
          offerApproved={offerApproved}
          showCreditStatus={offer?.financial.financePlan === "Monthly Plan"}
          disableEditEmail={hasSignedProposal}
          disableEditPhone={hasSignedProposal}
          onContactInfoSave={(updatedContact: Contact) =>
            onContactInfoSave(updatedContact, ROLES.HOMEOWNER)
          }
          onSelectionUpdate={handlePrimarySelectionUpdate}
          modals={[
            SAME_SIGNER_THEN_VOID_SUNRUN,
            SAME_SIGNER_THEN_REGEN_INFORM,
            VOID_SUNRUN,
            REGEN_INFORM,
            REGEN_CONFIRM,
            SAME_SIGNER_THEN_REGEN_CONFIRM,
          ]}
          disableEditName
          showAgreementSignatureWarning={true}
          headerBanner={
            <>
              <ContractReviewBanners
                messages={contractReviewMessages}
                category="customer_name_check"
                filter={(msg) => !isTrustAgreementNameMatchMessage(msg)}
                sessionDismissScopeId={proposalId}
              />
              {isNewHomes && (
                <Banner
                  bannerType={BannerTypes.InfoLight}
                  show={true}
                  showClose={false}
                >
                  <Typography color="text.primary" fontWeight="medium">
                    The individual executing this agreement must be a titled
                    owner of the property at the time of closing. Please ensure
                    the name below matches accordingly.
                  </Typography>
                </Banner>
              )}
            </>
          }
        />
      </div>
      <div ref={secondaryRef}>
        <ContactCardCheckout
          initialEditState={secondaryEditState}
          selectedContactId={selectedSecondaryContact?.contactId}
          toolTipText="Secondary signers are optional. If the secondary signer is added as a credit passer, they must be on the title of the home OR they must be a spouse/domestic partner of the title holder."
          contactList={contactList || []}
          contactType="secondary"
          hasOffer={hasSignedProposal}
          showCreditStatus={offer?.financial.financePlan === "Monthly Plan"}
          disableEditEmail={hasSignedProposal}
          disableEditPhone={hasSignedProposal}
          onContactInfoSave={(updatedContact: Contact) =>
            onContactInfoSave(updatedContact, ROLES.CONTRACTCOSIGNER)
          }
          onSelectionUpdate={handleSecondarySelectionUpdate}
          modals={[
            SAME_SIGNER_THEN_RESEND_SUNRUN,
            SAME_SIGNER,
            RESEND_SUNRUN_SECONDARY,
            REMOVE_SECONDARY,
          ]}
        />
      </div>
      {!isNewHomes && (
        <div ref={!selectedPrimaryContact ? refSetter : null}>
          <ContactCardCheckout
            disableEditEmail={hasSignedProposal}
            disableEditPhone={hasSignedProposal}
            selectedContactId={selectedUtilityContact?.contactId}
            toolTipText="The utility bill contact must exactly match the name/spelling on the household's utility bills. This contact will be the signer for the NEM agreements and apply for utility approval."
            contactList={contactList || []}
            contactType="utility_bill"
            hasOffer={isNEMSigned}
            showCreditStatus={offer?.financial.financePlan === "Monthly Plan"}
            onContactInfoSave={(updatedContact: Contact) =>
              onContactInfoSave(updatedContact, ROLES.UTILITYBILLCONTACT)
            }
            onSelectionUpdate={handleBillingSelectionUpdate}
            modals={[RESEND_SUNRUN_UTILITY]}
            headerBanner={
              <ContractReviewBanners
                messages={contractReviewMessages}
                category="nem_agreement_check"
                filter={(message) => isNemAgreementCheckMessage(message)}
                sessionDismissScopeId={proposalId}
              />
            }
          />
        </div>
      )}
      {(!!saveOverlayMessage ||
        !!isVoiding ||
        !!isRegeneratingDocuments ||
        updateContactLoading) && (
        <LoadingOverlay
          message={
            saveOverlayMessage ??
            (isVoiding
              ? "Voiding offers..."
              : isRegeneratingDocuments
              ? "Regenerating documents..."
              : `${isNew ? "Creating" : "Updating"} contact...`)
          }
          data-testid="loading-overlay"
        />
      )}
    </>
  );
};

export { CheckoutContacts };
export type { CheckoutContactsProps };
